import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, Spinner } from 'react-bootstrap';
import {
  downloadDataBackup,
  getDataSummary,
  restoreDataBackup,
  wipeOvrRecords,
} from '../services/api';
import { setAlert } from '../store/slices/alertSlice';
import ConfirmActionModal from './ConfirmActionModal';

const readApiError = async (error, fallback) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed.error || fallback;
    } catch (parseError) {
      return fallback;
    }
  }
  return data?.error || fallback;
};

const DataMaintenance = () => {
  const dispatch = useDispatch();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [restoreFormEl, setRestoreFormEl] = useState(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getDataSummary();
      setSummary(data);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Could not load data summary',
      }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const busy = backingUp || restoring || wiping;

  const closeConfirm = () => {
    if (busy) return;
    setPendingAction(null);
  };

  const runPendingAction = async () => {
    if (!pendingAction) return;
    const action = pendingAction;

    try {
      if (action.type === 'backup') {
        setBackingUp(true);
        const response = await downloadDataBackup();
        const blob = new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const disposition = response.headers?.['content-disposition'] || '';
        const match = disposition.match(/filename="([^"]+)"/);
        link.href = url;
        link.download = match?.[1] || `irs-backup-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        dispatch(setAlert({ type: 'success', message: 'Backup downloaded. Store this file in a safe place.' }));
      } else if (action.type === 'restore') {
        setRestoring(true);
        const body = new FormData();
        body.append('file', restoreFile);
        body.append('confirm', 'RESTORE');
        const { data } = await restoreDataBackup(body);
        dispatch(setAlert({ type: 'success', message: data.message || 'Backup restored.' }));
        setRestoreFile(null);
        setRestoreConfirm('');
        restoreFormEl?.reset();
        await loadSummary();
      } else if (action.type === 'reset') {
        setWiping(true);
        const { data } = await wipeOvrRecords('RESET SYSTEM');
        dispatch(setAlert({ type: 'success', message: data.message || 'System reset to default.' }));
        setWipeConfirm('');
        await loadSummary();
      }

      setPendingAction(null);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: action.type === 'backup'
          ? await readApiError(error, 'Could not create the backup')
          : (error.response?.data?.error || action.errorMessage || 'Action failed'),
      }));
    } finally {
      setBackingUp(false);
      setRestoring(false);
      setWiping(false);
    }
  };

  const handleBackup = () => {
    setPendingAction({
      type: 'backup',
      title: 'Download backup?',
      message: 'Create and download a full system backup file now?\nStore it in a safe place. It includes account and OVR data.',
      confirmLabel: 'Yes, download backup',
      variant: 'primary',
      errorMessage: 'Could not create the backup',
    });
  };

  const handleRestore = (event) => {
    event.preventDefault();
    setRestoreFormEl(event.target);
    if (!restoreFile) {
      dispatch(setAlert({ type: 'danger', message: 'Choose a backup file first.' }));
      return;
    }
    if (restoreConfirm.trim() !== 'RESTORE') {
      dispatch(setAlert({ type: 'danger', message: 'Type RESTORE to confirm.' }));
      return;
    }
    setPendingAction({
      type: 'restore',
      title: 'Restore backup?',
      message: `Replace current system data with "${restoreFile.name}"?\nThis cannot be undone easily. Sign in again if your account was replaced.`,
      confirmLabel: 'Yes, restore backup',
      variant: 'warning',
      errorMessage: 'Could not restore the backup',
    });
  };

  const handleWipe = (event) => {
    event.preventDefault();
    if (wipeConfirm.trim() !== 'RESET SYSTEM') {
      dispatch(setAlert({ type: 'danger', message: 'Type RESET SYSTEM to confirm.' }));
      return;
    }
    setPendingAction({
      type: 'reset',
      title: 'Reset system to default?',
      message: 'Factory reset will remove OVR records, other users, notifications, and audit logs.\nBrand and email templates return to defaults.\nYour Admin login, roles, departments, and SMTP settings stay.\n\nContinue?',
      confirmLabel: 'Yes, reset system',
      variant: 'danger',
      errorMessage: 'Could not reset the system',
    });
  };

  if (loading && !summary) {
    return (
      <div className="admin-page d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h3>Data Maintenance</h3>
          <p>
            Super admin tools to back up the system, restore a backup, or reset the system to default.
            Reset keeps your Admin account, roles, departments, and email SMTP settings.
          </p>
        </div>
      </div>

      {summary && (
        <div className="data-summary card p-3 shadow-sm">
          <span><strong>{summary.visibleIncidents ?? summary.incidents}</strong> visible incidents</span>
          <span><strong>{summary.hiddenIncidents || 0}</strong> archived</span>
          <span><strong>{summary.userComments}</strong> comments</span>
          <span><strong>{summary.notifications}</strong> incident notifications</span>
          <span><strong>{summary.users}</strong> users</span>
          <span><strong>{summary.departments}</strong> departments</span>
          <span><strong>{summary.roles}</strong> roles</span>
        </div>
      )}

      <section className="card p-3 shadow-sm data-tool-card">
        <h4>Backup</h4>
        <p>
          Download users, departments, OVR records, settings, and incident attachments.
          The file contains account data. Keep it private.
        </p>
        <Button type="button" onClick={handleBackup} disabled={busy}>
          {backingUp ? 'Preparing backup...' : 'Download backup'}
        </Button>
      </section>

      <Form onSubmit={handleRestore} className="card p-3 shadow-sm data-tool-card">
        <h4>Restore</h4>
        <p>
          Replace current records with a backup file. Sign in again if your account was replaced.
        </p>
        <Form.Group controlId="restore-file" className="mb-3">
          <Form.Label>Backup file</Form.Label>
          <Form.Control
            type="file"
            accept="application/json,.json"
            onChange={(event) => setRestoreFile(event.target.files?.[0] || null)}
          />
        </Form.Group>
        <Form.Group controlId="restore-confirm" className="mb-3">
          <Form.Label>Type RESTORE to confirm</Form.Label>
          <Form.Control
            value={restoreConfirm}
            onChange={(event) => setRestoreConfirm(event.target.value)}
            placeholder="RESTORE"
            autoComplete="off"
          />
        </Form.Group>
        <Button type="submit" variant="warning" disabled={busy}>
          {restoring ? 'Restoring...' : 'Restore backup'}
        </Button>
      </Form>

      <Form onSubmit={handleWipe} className="card p-3 shadow-sm data-tool-card data-tool-danger">
        <h4>Reset system to default</h4>
        <p>
          Factory reset: removes all OVR records and uploads, every other user account, notifications, and audit logs.
          Brand and email templates return to defaults. Your Admin login, roles, departments, and SMTP settings stay.
          Download a backup first if you may need the current data again.
        </p>
        <Form.Group controlId="wipe-confirm" className="mb-3">
          <Form.Label>Type RESET SYSTEM to confirm</Form.Label>
          <Form.Control
            value={wipeConfirm}
            onChange={(event) => setWipeConfirm(event.target.value)}
            placeholder="RESET SYSTEM"
            autoComplete="off"
          />
        </Form.Group>
        <Button type="submit" variant="danger" disabled={busy}>
          {wiping ? 'Resetting...' : 'Reset system to default'}
        </Button>
      </Form>

      <ConfirmActionModal
        show={Boolean(pendingAction)}
        title={pendingAction?.title}
        message={pendingAction?.message}
        confirmLabel={pendingAction?.confirmLabel}
        variant={pendingAction?.variant || 'primary'}
        busy={busy}
        onCancel={closeConfirm}
        onConfirm={runPendingAction}
      />
    </div>
  );
};

export default DataMaintenance;
